import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Users, Plus, Clock, FileText, Building2, Search, IdCard, Camera, Upload, X } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Link } from "react-router-dom";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const ORG_LABEL = {
  BPK: "BPK Klasis", BP2K: "BP2K / Pengawas",
  PERIA: "PERIA", PERNITA: "PERNITA", PEREDA: "PEREDA", PERSEMI: "PERSEMI",
  KOMISI: "Bagian Klasis", JEMAAT: "Gembala Jemaat",
};

export default function Pengurus() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [q, setQ] = useState("");
  const [showActive, setShowActive] = useState(true);
  const [open, setOpen] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null); // position to update photo
  const [uploadingId, setUploadingId] = useState(null);
  const [form, setForm] = useState({
    person_name: "", title: "", organization: "BPK", church_id: null,
    period_start: new Date().toISOString().slice(0,10), period_end: "",
    sk_number: "", sk_date: "", notes: "", photo_base64: "",
  });

  const canCreate = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    const [p, c] = await Promise.all([
      api.get("/positions"),
      api.get("/churches"),
    ]);
    setItems(p.data); setChurches(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0,10);
    const WADAH_SET = new Set(["PERIA","PERNITA","PEREDA","PERSEMI"]);
    return items.filter((p) => {
      if (filter === "WADAH") {
        if (!WADAH_SET.has(p.organization)) return false;
      } else if (filter === "BIDANG") {
        if (p.organization !== "KOMISI") return false;
      } else if (filter !== "ALL" && p.organization !== filter) {
        return false;
      }
      if (q && !(p.person_name + p.title).toLowerCase().includes(q.toLowerCase())) return false;
      if (showActive) {
        const active = p.period_start <= today && (!p.period_end || p.period_end >= today);
        if (!active) return false;
      }
      return true;
    });
  }, [items, filter, q, showActive]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((p) => { (g[p.organization] = g[p.organization] || []).push(p); });
    return g;
  }, [filtered]);

  const submit = async () => {
    if (!form.person_name || !form.title) { toast.error("Nama & jabatan wajib diisi."); return; }
    try {
      const payload = {
        ...form,
        church_id: form.church_id || null,
        period_end: form.period_end || null,
        sk_number: form.sk_number || null,
        sk_date: form.sk_date || null,
        notes: form.notes || null,
      };
      const created = await api.post("/positions", payload);
      // If photo was uploaded in the create form, apply it via profile endpoint
      if (form.photo_base64 && created.data?.id) {
        try {
          await api.patch(`/positions/${created.data.id}/profile`, { photo_base64: form.photo_base64, photo_url: null });
        } catch (e) { /* silent — pengurus already saved */ }
      }
      toast.success("Pengurus/pelayan berhasil ditambahkan.");
      setOpen(false);
      setForm({ person_name: "", title: "", organization: "BPK", church_id: null,
                period_start: new Date().toISOString().slice(0,10), period_end: "",
                sk_number: "", sk_date: "", notes: "", photo_base64: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadPhotoFor = async (positionId, file) => {
    if (!file) return;
    if (file.size > 1_800_000) { toast.error("Ukuran foto maksimal 1.5MB"); return; }
    setUploadingId(positionId);
    try {
      const b64 = await fileToBase64(file);
      await api.patch(`/positions/${positionId}/profile`, { photo_base64: b64, photo_url: null });
      toast.success("Foto pengurus diperbarui");
      setPhotoTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal upload foto");
    } finally { setUploadingId(null); }
  };

  const endTerm = async (p) => {
    if (!window.confirm(`Akhiri masa jabatan ${p.person_name}?`)) return;
    try {
      await api.delete(`/positions/${p.id}`);
      toast.success("Masa jabatan diakhiri (riwayat tetap tersimpan).");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Organisasi</div>
          <h1 className="font-heading font-bold text-3xl mt-1">Pengurus &amp; Pelayan</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            BPK, BP2K, Bidang/Komisi, dan Gembala Jemaat. Riwayat SK &amp; periode jabatan disimpan lengkap.
          </p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-new-position"><Plus className="h-4 w-4 mr-2" /> Tambah Pengurus</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Pengurus / Pelayan Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nama lengkap</Label><Input value={form.person_name} onChange={(e)=>setForm({...form, person_name:e.target.value})} data-testid="input-position-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Jabatan</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="input-position-title" /></div>
                  <div>
                    <Label>Organisasi</Label>
                    <Select value={form.organization} onValueChange={(v)=>setForm({...form, organization:v, church_id: v!=="JEMAAT" ? null : form.church_id})}>
                      <SelectTrigger data-testid="select-position-org"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ORG_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {form.organization === "JEMAAT" && (
                  <div>
                    <Label>Jemaat</Label>
                    <Select value={form.church_id || ""} onValueChange={(v)=>setForm({...form, church_id:v})}>
                      <SelectTrigger data-testid="select-position-church"><SelectValue placeholder="Pilih jemaat" /></SelectTrigger>
                      <SelectContent>
                        {churches.filter(c=>c.type==="JEMAAT").map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Mulai</Label><Input type="date" value={form.period_start} onChange={(e)=>setForm({...form, period_start:e.target.value})} data-testid="input-position-start" /></div>
                  <div><Label>Selesai (opsional)</Label><Input type="date" value={form.period_end} onChange={(e)=>setForm({...form, period_end:e.target.value})} data-testid="input-position-end" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>No. SK</Label><Input value={form.sk_number} onChange={(e)=>setForm({...form, sk_number:e.target.value})} data-testid="input-position-sk" /></div>
                  <div><Label>Tanggal SK</Label><Input type="date" value={form.sk_date} onChange={(e)=>setForm({...form, sk_date:e.target.value})} data-testid="input-position-skdate" /></div>
                </div>
                <div><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} /></div>
                {/* Photo upload (optional) */}
                <div>
                  <Label>Foto Pengurus (opsional)</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.photo_base64 ? (
                      <div className="relative">
                        <img src={form.photo_base64} alt="preview" className="h-16 w-16 rounded-md object-cover border-2 border-secondary" />
                        <button type="button" onClick={()=>setForm({...form, photo_base64:""})} className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full p-0.5" data-testid="btn-remove-preview-photo">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded-md border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                        <Camera className="h-5 w-5" />
                      </div>
                    )}
                    <label className="text-xs text-primary cursor-pointer hover:underline flex items-center gap-1" data-testid="label-upload-form-photo">
                      <Upload className="h-3.5 w-3.5" /> Pilih dari drive/laptop
                      <input type="file" accept="image/*" hidden data-testid="input-form-photo"
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          if (f.size > 1_800_000) { toast.error("Ukuran foto maksimal 1.5MB"); return; }
                          setForm({ ...form, photo_base64: await fileToBase64(f) });
                        }} />
                    </label>
                  </div>
                  <div className="text-[0.65rem] text-muted-foreground mt-1">Format JPG/PNG, maksimal ~1.5MB</div>
                </div>
              </div>
              <DialogFooter><Button onClick={submit} data-testid="btn-save-position">Simpan</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Cari nama / jabatan…" className="pl-9 w-72" data-testid="pengurus-search" />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showActive} onChange={(e)=>setShowActive(e.target.checked)} data-testid="filter-active" />
          Hanya aktif saat ini
        </label>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList data-testid="pengurus-tabs" className="flex flex-wrap h-auto">
          <TabsTrigger value="ALL">Semua</TabsTrigger>
          <TabsTrigger value="BPK" data-testid="tab-BPK">BPK Klasis</TabsTrigger>
          <TabsTrigger value="BP2K" data-testid="tab-BP2K">BP2K</TabsTrigger>
          <TabsTrigger value="WADAH" data-testid="tab-WADAH">Wadah</TabsTrigger>
          <TabsTrigger value="BIDANG" data-testid="tab-BIDANG">Bagian</TabsTrigger>
          <TabsTrigger value="JEMAAT" data-testid="tab-JEMAAT">Gembala Jemaat</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : Object.keys(grouped).length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center">
          <Users className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Tidak ada pengurus/pelayan sesuai filter.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([org, list]) => (
            <div key={org}>
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <div className="font-heading font-bold text-sm tracking-wide">{ORG_LABEL[org] || org}</div>
                <div className="text-xs text-muted-foreground">({list.length})</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((p) => {
                  const today = new Date().toISOString().slice(0,10);
                  const active = p.period_start <= today && (!p.period_end || p.period_end >= today);
                  const church = churches.find(c => c.id === p.church_id);
                  const photoSrc = p.photo_url || p.photo_base64;
                  const initials = (p.person_name || "").split(" ").slice(0,2).map(s=>s[0]).join("").toUpperCase();
                  return (
                    <Card key={p.id} className="hover:border-primary/30 transition-colors" data-testid={`row-position-${p.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <StatusBadge status={active ? "success" : "muted"} label={active ? "AKTIF" : "RIWAYAT"} />
                          <div className="flex items-center gap-2">
                            {canCreate && (
                              <button onClick={() => setPhotoTarget(p)} className="text-[0.65rem] text-muted-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid={`btn-photo-${p.id}`} title="Upload foto pengurus">
                                <Camera className="h-3 w-3" /> foto
                              </button>
                            )}
                            <Link to={`/kartu/${p.id}`} className="text-[0.65rem] text-primary hover:text-secondary transition-colors flex items-center gap-1" data-testid={`btn-kartu-${p.id}`} title="Kartu digital">
                              <IdCard className="h-3 w-3" /> kartu
                            </Link>
                            {canCreate && active && (
                              <button onClick={() => endTerm(p)} className="text-[0.65rem] text-muted-foreground hover:text-destructive transition-colors" data-testid={`btn-end-${p.id}`}>Akhiri</button>
                            )}
                          </div>
                        </div>
                        <Link to={`/kartu/${p.id}`} className="flex items-start gap-3">
                          {photoSrc ? (
                            <img src={photoSrc} alt={p.person_name} className="h-14 w-14 rounded-md object-cover border border-border shrink-0" data-testid={`img-avatar-${p.id}`} />
                          ) : (
                            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-sm shrink-0 border border-border">
                              {initials || "?"}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-heading font-bold text-sm hover:text-primary transition-colors truncate">{p.person_name}</div>
                            <div className="text-xs text-primary font-medium mt-0.5 truncate">{p.title}</div>
                            {church && <div className="text-xs text-muted-foreground mt-1 truncate">{church.name}</div>}
                          </div>
                        </Link>
                        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-2 text-[0.7rem] text-muted-foreground">
                          <div className="flex items-center gap-1"><Clock className="h-3 w-3" />
                            <span>{p.period_start} → {p.period_end || "…"}</span>
                          </div>
                          {p.sk_number && (
                            <div className="flex items-center gap-1 truncate"><FileText className="h-3 w-3 shrink-0" />
                              <span className="truncate">{p.sk_number}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Update Dialog */}
      {photoTarget && (
        <Dialog open={!!photoTarget} onOpenChange={(o)=>!o && setPhotoTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Foto Pengurus</DialogTitle>
              <DialogDescription>{photoTarget.person_name} — {photoTarget.title}</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-3">
              {(photoTarget.photo_url || photoTarget.photo_base64) ? (
                <img src={photoTarget.photo_url || photoTarget.photo_base64} alt={photoTarget.person_name} className="h-32 w-32 rounded-lg object-cover border-2 border-secondary" data-testid="img-current-photo" />
              ) : (
                <div className="h-32 w-32 rounded-lg bg-muted flex items-center justify-center">
                  <Camera className="h-10 w-10 text-muted-foreground" />
                </div>
              )}
              <label className="cursor-pointer" data-testid="label-upload-existing-photo">
                <Button asChild className="rounded-full" disabled={uploadingId === photoTarget.id}>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploadingId === photoTarget.id ? "Mengunggah…" : "Pilih dari drive/laptop"}
                  </span>
                </Button>
                <input type="file" accept="image/*" hidden data-testid="input-upload-existing-photo"
                  onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    uploadPhotoFor(photoTarget.id, f);
                  }} />
              </label>
              <div className="text-[0.65rem] text-muted-foreground text-center">Format JPG/PNG, maksimal ~1.5MB<br/>Foto langsung tampil di kartu digital.</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}
